import { uniq } from "lodash";

import { tryGetHexId } from "../humanHexIds.js";
import { HexId } from "../idTypeBrands.js";
import { HEX_PROJECT_TAG } from "../magic/inlineReferences.js";
import { notNullish } from "../filterGuards.js";

export const GUIDE_MARKDOWN_MONACO_LANG_ID = "guide-markdown";

const bracketedProjectRefRegex = new RegExp(
  String.raw`\[${HEX_PROJECT_TAG}:([a-zA-Z0-9_@-]{1,128})(?::[^\]]+)?\]`,
  "g",
);

type ProjectMentionRefRegistry = {
  toStableId: (args: {
    entityType: "Hex";
    shortRef: string;
  }) => Promise<string | null>;
} | null;

/**
 * Matches `@project <id>` mentions.
 * The id capture group accepts UUIDs, URL slugs, and compressed hex IDs.
 * Returns a fresh RegExp each call to avoid stale lastIndex state.
 */
export const getProjectMentionRegex = (): RegExp =>
  /@project\s+([a-zA-Z0-9-]+)/g;

export const getProjectMentionRegexForMonarch = (): RegExp =>
  // `@` has special meaning for monaco monarch languages,
  // so make a variant of the regex that escapes it.
  // See https://github.com/microsoft/vscode/blob/0a01b4f5ae8d31caa948826706da99696e9e77a6/src/vs/editor/standalone/common/monarch/monarchCompile.ts#L85
  // We also don't need a global regex for monaco monarch grammars.
  new RegExp(getProjectMentionRegex().source.replaceAll("@", "@@"));

/** Given a match from `getProjectMentionRegex()`, returns the hex ID it matches. */
export function parseProjectMentionMatch(
  match: RegExpMatchArray,
): HexId | null {
  const part = match[1];
  if (part == null) return null;

  // Eventually we might want to support matching an ID from a URL here too
  return tryGetHexId(part);
}

/**
 * Extracts unique hex project IDs from `@project <hexId>` mentions.
 * `hexId` can be a full hex UUID or a human hex id that we use in URLs.
 */
export function extractProjectMentionIds(text: string): HexId[] {
  const matches = [...text.matchAll(getProjectMentionRegex())];
  const hexIds = matches.map(parseProjectMentionMatch).filter(notNullish);
  return uniq(hexIds);
}

/**
 * Replaces `@project <id>` mentions with `[hexProject:<shortRef>]` tags.
 *
 * Mentions whose UUID is not present in `refMap` are left unchanged.
 */
export function substituteProjectMentions(
  text: string,
  refMap: Map<HexId, { shortRef: string }>,
): string {
  if (refMap.size === 0) {
    return text;
  }

  return text.replace(getProjectMentionRegex(), (match, rawId: string) => {
    const hexId = tryGetHexId(rawId);
    if (hexId == null) return match;

    const entry = refMap.get(hexId);
    if (!entry) return match;

    return `[${HEX_PROJECT_TAG}:${entry.shortRef}]`;
  });
}

export function substituteProjectMentionsWithFullIdShortRefs(
  text: string,
): string {
  const hexIds = extractProjectMentionIds(text);
  const refMap = new Map(hexIds.map((hexId) => [hexId, { shortRef: hexId }]));
  return substituteProjectMentions(text, refMap);
}

/**
 * Rewrites guide-local project references into persistent `@project <hexId>` mentions.
 */
export async function normalizeProjectMentions({
  refRegistry,
  text,
}: {
  refRegistry: ProjectMentionRefRegistry;
  text: string;
}): Promise<{ normalizedText: string; unresolvedProjectRefs: string[] }> {
  const bracketedTokens = [...text.matchAll(bracketedProjectRefRegex)]
    .map((match) => match[1])
    .filter((token): token is string => token != null);
  const mentionTokens = [...text.matchAll(getProjectMentionRegex())]
    .map((match) => match[1])
    .filter((token): token is string => token != null);
  const tokens = Array.from(new Set([...bracketedTokens, ...mentionTokens]));

  const resolvedProjectIds = new Map<string, HexId>();
  const unresolvedProjectRefs = new Set<string>();

  for (const token of tokens) {
    const directHexId = tryGetHexId(token);
    if (directHexId != null) {
      resolvedProjectIds.set(token, directHexId);
      continue;
    }

    if (refRegistry == null) {
      unresolvedProjectRefs.add(token);
      continue;
    }

    const stableId = await refRegistry.toStableId({
      entityType: "Hex",
      shortRef: token,
    });
    if (stableId == null) {
      unresolvedProjectRefs.add(token);
      continue;
    }

    resolvedProjectIds.set(token, HexId.check(stableId));
  }

  const normalizedBracketedText = text.replace(
    bracketedProjectRefRegex,
    (match, rawToken: string) => {
      const resolvedProjectId = resolvedProjectIds.get(rawToken);
      return resolvedProjectId == null
        ? match
        : `@project ${resolvedProjectId}`;
    },
  );

  const normalizedText = normalizedBracketedText.replace(
    getProjectMentionRegex(),
    (match, rawToken: string) => {
      const resolvedProjectId = resolvedProjectIds.get(rawToken);
      return resolvedProjectId == null
        ? match
        : `@project ${resolvedProjectId}`;
    },
  );

  return {
    normalizedText,
    unresolvedProjectRefs: [...unresolvedProjectRefs].sort(),
  };
}

const isAiderSearchMarker = (line: string): boolean =>
  /^<{5,9}\s*SEARCH>?$/.test(line.trim());

const isAiderSeparatorMarker = (line: string): boolean =>
  line.trimStart().startsWith("=======") && /^={5,9}\s*$/.test(line.trim());

const isAiderReplaceMarker = (line: string): boolean =>
  /^>{5,9}\s*REPLACE$/.test(line.trim());

/**
 * Normalizes project references in Aider replacement blocks without changing search anchors.
 */
export async function normalizeProjectMentionsInAiderDiff({
  diff,
  refRegistry,
}: {
  diff: string;
  refRegistry: ProjectMentionRefRegistry;
}): Promise<{ normalizedDiff: string; unresolvedProjectRefs: string[] }> {
  const normalizedLines: string[] = [];
  const replaceLines: string[] = [];
  const unresolvedProjectRefs = new Set<string>();
  let mode: "none" | "search" | "replace" = "none";

  const flushReplaceLines = async (): Promise<void> => {
    const { normalizedText, unresolvedProjectRefs: unresolvedRefs } =
      await normalizeProjectMentions({
        refRegistry,
        text: replaceLines.join("\n"),
      });

    for (const unresolvedRef of unresolvedRefs) {
      unresolvedProjectRefs.add(unresolvedRef);
    }
    normalizedLines.push(...normalizedText.split("\n"));
    replaceLines.length = 0;
  };

  for (const line of diff.split("\n")) {
    if (isAiderSearchMarker(line)) {
      if (mode === "replace") {
        await flushReplaceLines();
      }
      mode = "search";
      normalizedLines.push(line);
      continue;
    }

    if (mode === "search" && isAiderSeparatorMarker(line)) {
      mode = "replace";
      normalizedLines.push(line);
      continue;
    }

    if (mode === "replace" && isAiderReplaceMarker(line)) {
      await flushReplaceLines();
      mode = "none";
      normalizedLines.push(line);
      continue;
    }

    if (mode === "replace") {
      replaceLines.push(line);
    } else {
      normalizedLines.push(line);
    }
  }

  if (mode === "replace") {
    await flushReplaceLines();
  }

  return {
    normalizedDiff: normalizedLines.join("\n"),
    unresolvedProjectRefs: [...unresolvedProjectRefs].sort(),
  };
}
